<template>
  <div class="account-emails">
    <v-progress-linear
      v-if="loading && !emails.length"
      :indeterminate="true"
      color="success"
    ></v-progress-linear>
    <div v-if="!loading && !emails.length" class="account-comm-empty">
      No emails
    </div>
    <div
      v-for="(email, index) in emails"
      :key="email.id || index"
      class="account-comm-item"
      :class="{ 'account-comm-item--open': email.expanded }"
    >
      <div class="account-comm-item-header" @click="toggle(email)">
        <div
          class="account-comm-item-avatar"
          :class="
            isIncoming(email)
              ? 'account-comm-item-avatar--in'
              : 'account-comm-item-avatar--out'
          "
        >
          <v-icon small dark>
            {{ isIncoming(email) ? "mdi-download" : "mdi-upload" }}
          </v-icon>
        </div>
        <div class="account-comm-item-summary">
          <div class="account-comm-item-title-row">
            <span class="account-comm-item-title">
              {{ isIncoming(email) ? email.from : email.to }}
            </span>
            <v-chip
              small
              text-color="white"
              :color="isIncoming(email) ? '#1e88e5' : '#43a047'"
              class="account-comm-direction-chip"
            >
              {{ isIncoming(email) ? "Incoming" : "Outgoing" }}
            </v-chip>
            <v-icon
              v-if="email.hasAttachments"
              small
              color="#607d8b"
              class="account-comm-attach-icon"
            >
              mdi-paperclip
            </v-icon>
          </div>
          <div v-if="email.subject" class="account-comm-item-subject">
            {{ email.subject }}
          </div>
          <div class="account-comm-item-preview">
            {{ truncateText(email) }}
          </div>
        </div>
        <div class="account-comm-item-meta">
          <div class="account-comm-item-date">{{ email.date | time }}</div>
          <v-chip
            v-if="email.communicationStatus === 'PENDING'"
            small
            text-color="white"
            color="#fdb400"
            class="account-comm-chip"
          >
            Pending
          </v-chip>
        </div>
        <v-icon class="account-comm-item-chevron">
          {{ email.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
      </div>

      <div v-if="email.expanded" class="account-comm-item-body">
        <div
          v-if="isIncoming(email)"
          class="account-comm-actions account-comm-actions--top"
        >
          <v-btn color="success" outline small @click="openReply(email, false)">
            <v-icon small class="mr-1">mdi-reply</v-icon>
            Reply
          </v-btn>
          <v-btn color="success" outline small class="ml-2" @click="openReply(email, true)">
            <v-icon small class="mr-1">mdi-reply-all</v-icon>
            Reply all
          </v-btn>
          <v-btn
            v-if="
              email.communicationID && email.communicationStatus === 'PENDING'
            "
            color="success"
            small
            outline
            :loading="completeProcessing && completeTarget === email"
            @click="openCompleteDialog(email)"
          >
            <v-icon small class="mr-1">mdi-check</v-icon>
            Mark done
          </v-btn>
        </div>

        <div v-if="email.subject" class="account-comm-subject-heading">
          {{ email.subject }}
        </div>
        <div class="account-comm-detail-grid">
          <div class="account-comm-detail">
            <span class="account-comm-detail-label">From</span>
            <span class="account-comm-detail-value">{{ email.from }}</span>
          </div>
          <div class="account-comm-detail">
            <span class="account-comm-detail-label">To</span>
            <span class="account-comm-detail-value">{{ email.to }}</span>
          </div>
          <div v-if="email.cc" class="account-comm-detail">
            <span class="account-comm-detail-label">Cc</span>
            <span class="account-comm-detail-value">{{ email.cc }}</span>
          </div>
          <div v-if="email.bcc" class="account-comm-detail">
            <span class="account-comm-detail-label">Bcc</span>
            <span class="account-comm-detail-value">{{ email.bcc }}</span>
          </div>
          <div class="account-comm-detail">
            <span class="account-comm-detail-label">Type</span>
            <span class="account-comm-detail-value">{{ email.type }}</span>
          </div>
          <div class="account-comm-detail">
            <span class="account-comm-detail-label">Status</span>
            <span class="account-comm-detail-value">{{ email.status }}</span>
          </div>
        </div>

        <email-body :row="email" :is-incoming="isIncoming(email)"></email-body>

        <div
          v-if="email.showReply"
          :id="'account-email-reply-' + email.id"
          class="account-comm-reply"
        >
          <div class="account-comm-reply-header" dark>
            <v-icon small class="mr-1">{{
              email.replyAll ? "mdi-reply-all" : "mdi-reply"
            }}</v-icon>
            {{ email.replyAll ? "Reply all" : "Reply to " + email.from }}
          </div>
          <div v-if="email.replyAll && email.cc" class="account-comm-detail mb-2">
            <span class="account-comm-detail-label">Cc</span>
            <span class="account-comm-detail-value">{{ email.cc }}</span>
          </div>
          <v-textarea
            v-model="email.replyBody"
            placeholder="Type your reply..."
            auto-grow
            hide-details
            rows="3"
            class="account-comm-reply-textarea"
          ></v-textarea>
          <v-layout row justify-end class="mt-2">
            <v-btn color="default" dark small @click="toggleReply(email)">
              Cancel
            </v-btn>
            <v-btn
              color="success"
              small
              class="ml-2 mr-3"
              :loading="processingId === email.id"
              :disabled="!email.replyBody || !email.replyBody.trim()"
              @click="sendReply(email)"
            >
              <v-icon small class="mr-1">mdi-send</v-icon>
              Send reply
            </v-btn>
          </v-layout>
        </div>
      </div>
    </div>
    <div v-if="hasMore" class="account-comm-load-more">
      <v-btn
        small
        outline
        color="success"
        :loading="loadingMore"
        @click="loadMore"
      >
        Load more emails
      </v-btn>
    </div>

    <v-dialog v-model="completeDialog" persistent max-width="480">
      <v-card>
        <v-card-title class="account-comm-complete-header">
          <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
          Mark done
        </v-card-title>
        <v-card-text class="pt-4">
          <v-textarea
            v-model="completeNote"
            label="Note (required)"
            hint="A note is required to mark a communication as done."
            persistent-hint
            outline
            rows="3"
            autofocus
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelCompleteDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :disabled="!completeNote || !completeNote.trim()"
            :loading="completeProcessing"
            @click="confirmComplete"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { truncate } from "../../utils/helpers.js";

const PAGE_SIZE = 25;

export default {
  data() {
    return {
      emails: [],
      totalCount: 0,
      pendingCount: 0,
      pendingFromApi: false,
      loading: true,
      loadingMore: false,
      hasMore: false,
      processingId: null,
      user: "",
      completeDialog: false,
      completeNote: "",
      completeTarget: null,
      completeProcessing: false,
    };
  },
  async created() {
    this.user = this.$store.state.authentication.user;
    await this.loadCount();
    await this.loadEmails();
  },
  methods: {
    applyCount(response) {
      if (response && typeof response === "object") {
        this.totalCount = response.total || 0;
        this.pendingCount = response.pending || 0;
        this.pendingFromApi = true;
      } else {
        this.totalCount = response || 0;
        this.pendingFromApi = false;
      }
    },
    async loadCount() {
      try {
        this.applyCount(
          await this.$http.get(
            "api/emails/getCountForAccount?accountID=" + this.$route.params.id
          )
        );
      } catch (e) {
        this.totalCount = 0;
        this.pendingCount = 0;
      }

      this.$emit("loaded", this.totalCount, this.pendingCount);
    },
    async loadEmails() {
      this.loading = true;

      try {
        const emails = await this.$http.get(
          "api/emails/getForAccount?accountID=" +
            this.$route.params.id +
            "&skip=0&top=" +
            PAGE_SIZE
        );
        this.emails = (emails || []).map((email) => {
          email.showText = false;

          return email;
        });
        this.hasMore = (emails || []).length === PAGE_SIZE;
      } catch (e) {
        this.emails = [];
        this.hasMore = false;
      }

      if (!this.pendingFromApi) {
        this.pendingCount = (this.emails || []).filter(function(email) {
          return email.communicationStatus === "PENDING";
        }).length;
      }

      this.$emit("loaded", this.totalCount, this.pendingCount);
      this.loading = false;
    },
    async loadMore() {
      this.loadingMore = true;

      try {
        const emails = await this.$http.get(
          "api/emails/getForAccount?accountID=" +
            this.$route.params.id +
            "&skip=" +
            this.emails.length +
            "&top=" +
            PAGE_SIZE
        );
        const newEmails = (emails || []).map((email) => {
          email.showText = false;

          return email;
        });
        this.emails = this.emails.concat(newEmails);
        this.hasMore = newEmails.length === PAGE_SIZE;
      } catch (e) {
        this.hasMore = false;
      }

      this.loadingMore = false;
    },
    isIncoming(email) {
      return email && (email.direction === "IN" || email.status === "RECEIVED");
    },
    truncateText(email) {
      return truncate(email.text, 90);
    },
    toggle(email) {
      this.$set(email, "expanded", !email.expanded);
    },
    toggleReply(email) {
      this.$set(email, "showReply", !email.showReply);
      if (!email.replyBody) {
        this.$set(email, "replyBody", "");
      }
    },
    openReply(email, replyAll) {
      this.$set(email, "showReply", true);
      this.$set(email, "replyAll", !!replyAll);
      if (!email.replyBody) {
        this.$set(email, "replyBody", "");
      }
      var self = this;
      this.$nextTick(function() {
        self.scrollToReply(email);
      });
    },
    scrollToReply(email) {
      var el = this.$el.querySelector("#account-email-reply-" + email.id);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      var textarea = el && el.querySelector("textarea");
      if (textarea && textarea.focus) {
        textarea.focus();
      }
    },
    async sendReply(email) {
      if (!email.replyBody || !email.replyBody.trim()) {
        return;
      }

      this.processingId = email.id;

      try {
        await this.$http.post(
          "api/emails/reply",
          {
            emailID: email.id,
            to: email.from,
            cc: email.replyAll ? email.cc : "",
            body: email.replyBody,
            note: email.replyBody,
            user: this.user,
            markCompleted: true,
            communicationID: email.communicationID,
          },
          { notifyError: false, timeout: 120000 }
        );
        this.$notify({
          group: "notification",
          text: "Reply sent",
          type: "success",
        });
        email.showReply = false;
        email.replyBody = "";
        try {
          await this.loadCount();
          await this.loadEmails();
        } catch (e) {}
      } catch (e) {
        this.$notify({
          group: "notification",
          text: (e && e.message) || "Could not send reply",
          type: "error",
        });
      }

      this.processingId = null;
    },
    openCompleteDialog(email) {
      this.completeTarget = email;
      this.completeNote = "";
      this.completeDialog = true;
    },
    cancelCompleteDialog() {
      this.completeDialog = false;
      this.completeTarget = null;
      this.completeNote = "";
    },
    async confirmComplete() {
      if (
        !this.completeNote ||
        !this.completeNote.trim() ||
        !this.completeTarget
      ) {
        this.$notify({
          group: "notification",
          text: "You can't mark as completed without entering a note.",
          type: "error",
        });
        return;
      }

      var email = this.completeTarget;
      this.completeProcessing = true;

      try {
        await this.$http.post("api/communications/updateStatus", {
          communicationID: email.communicationID,
          status: "COMPLETED",
          note: this.completeNote,
          user: this.user,
        });
        this.$notify({
          group: "notification",
          text: "Email marked done",
          type: "success",
        });
        this.completeDialog = false;
        this.completeTarget = null;
        this.completeNote = "";
        await this.loadCount();
        await this.loadEmails();
      } catch (e) {
        this.$notify({
          group: "notification",
          text: "Could not update email",
          type: "error",
        });
      }

      this.completeProcessing = false;
    },
  },
};
</script>
<style>
.account-emails {
  max-height: 600px;
  overflow-y: auto;
  padding: 6px;
}

.account-comm-empty {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9e9e9e;
  font-size: 15px;
}

.account-comm-item {
  background: #fff;
  border: 1px solid #e3e8eb;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  overflow: hidden;
}

.account-comm-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #cfd8dc;
}

.account-comm-item--open {
  border-color: #81c784;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.12);
}

.account-comm-item-header {
  cursor: pointer;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.account-comm-item-avatar {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.account-comm-item-avatar--in {
  background: #1e88e5;
}

.account-comm-item-avatar--out {
  background: #43a047;
}

.account-comm-item-summary {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.account-comm-item-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-comm-item-title {
  font-weight: 600;
  font-size: 14px;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-comm-direction-chip {
  flex: 0 0 auto;
  font-size: 10px;
  height: 18px;
}

.account-comm-direction-chip .v-chip__content {
  height: 18px;
  padding: 0 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.account-comm-attach-icon {
  flex: 0 0 auto;
}

.account-comm-item-subject {
  font-size: 13px;
  color: #37474f;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.account-comm-item-preview {
  font-size: 12.5px;
  color: #90a4ae;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.account-comm-item-meta {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.account-comm-item-date {
  font-size: 15px;
  font-weight: 500;
  color: #455a64;
  white-space: nowrap;
}

.account-comm-chip {
  margin: 0;
  height: 20px;
}

.account-comm-chip .v-chip__content {
  height: 20px;
  font-size: 11px;
}

.account-comm-item-chevron {
  flex: 0 0 auto;
  color: #b0bec5;
}

.account-comm-item-body {
  padding: 10px 16px 12px 16px;
  border-top: 1px solid #f0f2f3;
  background: #fafcfb;
}

.account-comm-subject-heading {
  font-size: 15px;
  font-weight: 600;
  color: #263238;
  margin-bottom: 6px;
}

.account-comm-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 20px;
  background: #fff;
  border: 1px solid #eceff1;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 6px;
}

.account-comm-detail {
  font-size: 12.5px;
  color: #37474f;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-comm-detail-label {
  color: #90a4ae;
  font-weight: 600;
  margin-right: 5px;
}

.account-comm-detail-value {
  color: #37474f;
}

.account-comm-actions {
  margin-top: 10px;
}

.account-comm-actions--top {
  margin-top: 0;
  margin-bottom: 10px;
}

.account-comm-complete-header {
  background: #4caf50;
  color: #fff;
  font-size: 18px;
  font-weight: 500;
}

.account-comm-actions .v-btn {
  margin-left: 0;
  margin-right: 8px;
}

.account-comm-reply {
  margin-top: 12px;
  background: #f7fdf2;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  padding: 10px 12px;
}

.account-comm-reply-header {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.account-comm-reply-textarea textarea {
  background: #fff;
}

.account-emails .email-html-frame {
  height: 280px;
  min-height: 140px;
}

.account-emails .email-body {
  padding: 4px 0 0;
  max-width: none;
}

.account-comm-load-more {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
}
</style>
